/**
 * Transition pass — scans the grid and replaces edge tiles with proper
 * transition variants using an autotile bitmask.
 *
 * Bitmask layout (4-bit cardinal):
 *   N=1, E=2, S=4, W=8  → 16 combinations (0–15)
 *
 * The tileMapping tells us which tile ID to use for each terrain type
 * at each bitmask value.
 */
export class TransitionPass {
  /**
   * @param {Array<Array<number>>} grid
   * @param {number} width
   * @param {number} height
   * @param {Object} tileMapping — keyed by terrain/zone ID:
   *   { [terrainId]: { center, transitions: { 0: tileId, 1: tileId, … 15: tileId } } }
   *   If a mapping has no `transitions`, the terrain is left as-is.
   * @returns {Array<Array<number>>} — mutated grid (also returns reference)
   */
  process(grid, width, height, tileMapping) {
    if (!tileMapping) return grid;

    // Work on a copy so reads see the original neighbours
    const snapshot = grid.map(row => [...row]);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const terrainId = snapshot[y][x];
        const mapping = tileMapping[terrainId];
        if (!mapping || !mapping.transitions) continue;

        const bitmask = this._calculateBitmask(snapshot, x, y, terrainId, width, height);

        if (bitmask === 0) {
          // Fully surrounded by same terrain → center tile
          grid[y][x] = mapping.center !== undefined ? mapping.center : terrainId;
        } else {
          grid[y][x] = this._getTransitionTile(terrainId, bitmask, mapping);
        }
      }
    }

    return grid;
  }

  /**
   * Calculate 4-bit bitmask: which cardinal neighbours are a *different* terrain.
   * N=1, E=2, S=4, W=8
   */
  _calculateBitmask(grid, x, y, terrainId, width, height) {
    let mask = 0;
    // North
    if (y === 0 || grid[y - 1][x] !== terrainId) mask |= 1;
    // East
    if (x === width - 1 || grid[y][x + 1] !== terrainId) mask |= 2;
    // South
    if (y === height - 1 || grid[y + 1][x] !== terrainId) mask |= 4;
    // West
    if (x === 0 || grid[y][x - 1] !== terrainId) mask |= 8;
    return mask;
  }

  /**
   * Resolve bitmask to a tile ID via the mapping.
   * Falls back to the terrain ID itself if no mapping entry exists.
   */
  _getTransitionTile(terrainId, bitmask, mapping) {
    if (mapping.transitions && mapping.transitions[bitmask] !== undefined) {
      return mapping.transitions[bitmask];
    }
    return mapping.center !== undefined ? mapping.center : terrainId;
  }
}
